import { createReducer } from 'src/helpers/reducer';
import {
  ITimesheetsState,
  ILoadDataSuccessPayload,
  ILoadItemSuccessPayload,
  IMoveTimesheetToResolvedPayload,
} from './types';
import actionsTypes from './actionTypes';

const initState: ITimesheetsState = {
  pendingTimesheets: undefined,
  resolvedTimesheets: undefined,
  attentionRequiredTimesheets: undefined,
  isLoading: false,
  isRequiredAttentionLoading: false,
  isPendingLoading: false,
  isResolvedLoading: false,
  total: 0,
  isEditHoursModalVisible: false,
  isEditHoursLoading: false,
  filterApplied: false,
  pendingBulkActions: undefined,
  resolvedBulkActions: undefined,
  attentionRequiredBulkActions: undefined,
  requiredAttentionFilteredTotal: 0,
  pendingFilteredTotal: 0,
  resolvedFilteredTotal: 0,
  requiredAttentionTotalCount: 0,
  pendingTotalCount: 0,
  resolvedTotalCount: 0,
};

function onLoadTimesheetsRequest(state: ITimesheetsState, { payload }: any): ITimesheetsState {
  const { type } = payload;

  return {
    ...state,
    isLoading: true,
    isRequiredAttentionLoading: type === 'attention_required' ? true : state.isRequiredAttentionLoading,
    isPendingLoading: type === 'open' ? true : state.isPendingLoading,
    isResolvedLoading: type === 'past' ? true : state.isResolvedLoading,
  };
}

function onLoadTimesheetsSuccess(
  state: ITimesheetsState,
  { payload }: { payload: ILoadDataSuccessPayload },
): ITimesheetsState {
  const { timesheets, total, filteredTotal, totalCount, filterApplied, bulkActions, type } = payload;
  const {
    pendingTimesheets,
    resolvedTimesheets,
    attentionRequiredTimesheets,
    pendingFilteredTotal,
    resolvedFilteredTotal,
    requiredAttentionFilteredTotal,
    pendingTotalCount,
    resolvedTotalCount,
    requiredAttentionTotalCount,
  } = state;

  const mapTimesheets = (list: typeof timesheets | undefined) =>
    list?.map(timesheet => ({
      ...timesheet,
      children:
        timesheet?.children?.map(item => ({ ...item, job_id: timesheet.id, time_zone: timesheet.time_zone })) || [],
    }));

  return {
    ...state,
    pendingTimesheets: type === 'open' ? mapTimesheets(timesheets) : pendingTimesheets,
    resolvedTimesheets: type === 'past' ? mapTimesheets(timesheets) : resolvedTimesheets,
    attentionRequiredTimesheets:
      type === 'attention_required' ? mapTimesheets(timesheets) : attentionRequiredTimesheets,
    total,
    pendingFilteredTotal: type === 'open' ? filteredTotal ?? 0 : pendingFilteredTotal,
    resolvedFilteredTotal: type === 'past' ? filteredTotal ?? 0 : resolvedFilteredTotal,
    requiredAttentionFilteredTotal: type === 'attention_required' ? filteredTotal ?? 0 : requiredAttentionFilteredTotal,
    pendingTotalCount: type === 'open' ? totalCount ?? 0 : pendingTotalCount,
    resolvedTotalCount: type === 'past' ? totalCount ?? 0 : resolvedTotalCount,
    requiredAttentionTotalCount: type === 'attention_required' ? totalCount ?? 0 : requiredAttentionTotalCount,
    isLoading: false,
    isPendingLoading: type === 'open' ? false : state.isPendingLoading,
    isResolvedLoading: type === 'past' ? false : state.isResolvedLoading,
    isRequiredAttentionLoading: type === 'attention_required' ? false : state.isRequiredAttentionLoading,
    filterApplied,
    pendingBulkActions: type === 'open' ? bulkActions : state.pendingBulkActions,
    resolvedBulkActions: type === 'past' ? bulkActions : state.resolvedBulkActions,
    attentionRequiredBulkActions: type === 'attention_required' ? bulkActions : state.attentionRequiredBulkActions,
  };
}

function onLoadTimesheetsError(state: ITimesheetsState) {
  return {
    ...state,
    isLoading: false,
    isPendingLoading: false,
    isResolvedLoading: false,
    isRequiredAttentionLoading: false,
  };
}

function onProcessActionRequest(state: ITimesheetsState) {
  return {
    ...state,
    isLoading: true,
  };
}

function onProcessActionSuccess(state: ITimesheetsState, { payload }: { payload: ILoadItemSuccessPayload }) {
  const { pendingTimesheets } = state;
  const { job } = payload;

  const children = job?.children || job?.contested_hours;

  return {
    ...state,
    isLoading: false,
    pendingTimesheets: pendingTimesheets?.map(timesheet => ({
      ...timesheet,
      children:
        timesheet.id === job?.id
          ? children?.map(item => ({ ...item, job_id: timesheet.id, time_zone: timesheet.time_zone })) || []
          : timesheet.children,
    })),
  };
}

function onMoveTimesheetToResolved(
  state: ITimesheetsState,
  { payload }: { payload: IMoveTimesheetToResolvedPayload },
): ITimesheetsState {
  const { job, sourceType } = payload;
  const jobId = job.id;

  const getSourceList = () => {
    if (sourceType === 'open') {
      return state.pendingTimesheets;
    }

    if (sourceType === 'attention_required') {
      return state.attentionRequiredTimesheets;
    }

    return undefined;
  };

  const sourceList = getSourceList();
  const movedTimesheet = sourceList?.find(t => t.id === jobId);
  const filteredSource = sourceList?.filter(t => t.id !== jobId);

  const resolvedTimesheet = {
    ...movedTimesheet,
    ...job,
    children:
      (job.children || job.contested_hours)?.map(item => ({
        ...item,
        job_id: jobId,
        time_zone: movedTimesheet?.time_zone,
      })) || [],
  } as ITimesheetsState['resolvedTimesheets'] extends (infer T)[] | undefined ? T : never;

  return {
    ...state,
    isLoading: false,
    pendingTimesheets: sourceType === 'open' ? filteredSource : state.pendingTimesheets,
    attentionRequiredTimesheets:
      sourceType === 'attention_required' ? filteredSource : state.attentionRequiredTimesheets,
    resolvedTimesheets: [resolvedTimesheet, ...(state.resolvedTimesheets || [])],
    pendingFilteredTotal: sourceType === 'open' ? state.pendingFilteredTotal - 1 : state.pendingFilteredTotal,
    pendingTotalCount: sourceType === 'open' ? state.pendingTotalCount - 1 : state.pendingTotalCount,
    requiredAttentionFilteredTotal:
      sourceType === 'attention_required'
        ? state.requiredAttentionFilteredTotal - 1
        : state.requiredAttentionFilteredTotal,
    requiredAttentionTotalCount:
      sourceType === 'attention_required' ? state.requiredAttentionTotalCount - 1 : state.requiredAttentionTotalCount,
    resolvedFilteredTotal: state.resolvedFilteredTotal + 1,
    resolvedTotalCount: state.resolvedTotalCount + 1,
  };
}

function onProcessActionError(state: ITimesheetsState) {
  return {
    ...state,
    isLoading: false,
  };
}

function onLoadTimesheetItemRequest(state: ITimesheetsState): ITimesheetsState {
  return {
    ...state,
    isLoading: true,
  };
}

function onLoadTimesheetItemSuccess(
  state: ITimesheetsState,
  { payload }: { payload: ILoadItemSuccessPayload },
): ITimesheetsState {
  const { pendingTimesheets } = state;
  const { id, job } = payload;

  return {
    ...state,
    isLoading: false,
    pendingTimesheets: pendingTimesheets?.map(timesheet => ({
      ...timesheet,
      children:
        timesheet.id === id
          ? job.contested_hours.map(item => ({ ...item, job, time_zone: timesheet.time_zone }))
          : timesheet.children,
    })),
  };
}

function onLoadTimesheetItemError(state: ITimesheetsState) {
  return {
    ...state,
    isLoading: false,
  };
}

function changeEditHoursModalVisibility(state: ITimesheetsState) {
  return {
    ...state,
    isEditHoursModalVisible: !state.isEditHoursModalVisible,
  };
}

export default createReducer(initState, {
  [actionsTypes.LOAD_TIMESHEETS_REQUEST]: onLoadTimesheetsRequest,
  [actionsTypes.LOAD_TIMESHEETS_SUCCESS]: onLoadTimesheetsSuccess,
  [actionsTypes.LOAD_TIMESHEETS_ERROR]: onLoadTimesheetsError,
  [actionsTypes.PROCESS_ACTION_REQUEST]: onProcessActionRequest,
  [actionsTypes.PROCESS_ACTION_SUCCESS]: onProcessActionSuccess,
  [actionsTypes.PROCESS_ACTION_ERROR]: onProcessActionError,
  [actionsTypes.LOAD_TIMESHEET_ITEM_REQUEST]: onLoadTimesheetItemRequest,
  [actionsTypes.LOAD_TIMESHEET_ITEM_SUCCESS]: onLoadTimesheetItemSuccess,
  [actionsTypes.LOAD_TIMESHEET_ITEM_ERROR]: onLoadTimesheetItemError,
  [actionsTypes.MOVE_TIMESHEET_TO_RESOLVED]: onMoveTimesheetToResolved,
  [actionsTypes.CHANGE_EDIT_HOURS_MODAL_VISIBILITY]: changeEditHoursModalVisibility,
});
